/**
 * VÉRAULT Portal — NewClientModal
 *
 * The single canonical surface for creating a new client (household head OR
 * household member) record. Used from Clients list, Journeys list, Journey ·
 * Travelers tab, and anywhere else.
 *
 * Pass B unification: every "add client" affordance funnels through this modal
 * → POST /api/travels/clients with optional `household_client_id` linking
 * household members to a household head. No more separate "New traveler" flow.
 *
 * Props:
 *   · open, onClose, onCreated, navigateToProfile
 *   · defaultHouseholdHead — pre-selects a household head (e.g. inside a Journey)
 *   · lockHouseholdHead — locks the household-head picker (Journey context)
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, UserPlus, AlertCircle } from "lucide-react";
import { api, PortalButton } from "../index";
import { toast } from "sonner";

const inputClass =
  "w-full px-3 py-2 text-[14px] bg-[hsl(var(--background-paper))] border border-transparent rounded-md outline-none focus:border-[rgba(0,0,0,0.12)] focus:bg-white transition-colors placeholder:text-[hsl(var(--foreground-muted))]";
const labelClass =
  "font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1.5 block";

function normalizeError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback || "Save failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || d?.message || JSON.stringify(d)).join(", ") || fallback;
  }
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return String(detail);
}

export default function NewClientModal({
  open,
  onClose,
  onCreated,
  navigateToProfile = true,
  defaultHouseholdHead = "",
  lockHouseholdHead = false,
}) {
  const navigate = useNavigate();
  const [fullName, setFullName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [householdLabel, setHouseholdLabel] = React.useState("");
  const [householdHead, setHouseholdHead] = React.useState(defaultHouseholdHead || "");
  const [saving, setSaving] = React.useState(false);
  const [match, setMatch] = React.useState(null);
  const [existing, setExisting] = React.useState([]);

  React.useEffect(() => {
    if (!open) {
      setFullName("");
      setEmail("");
      setPhone("");
      setHouseholdLabel("");
      setHouseholdHead(defaultHouseholdHead || "");
      setMatch(null);
      setSaving(false);
      return;
    }
    // Pre-fetch the existing client list for soft de-dup + household head picker
    api
      .get("/travels/clients")
      .then(({ data }) => setExisting(Array.isArray(data) ? data : []))
      .catch(() => setExisting([]));
    setHouseholdHead(defaultHouseholdHead || "");
  }, [open, defaultHouseholdHead]);

  // Soft duplicate check as the advisor types
  React.useEffect(() => {
    if (!existing || existing.length === 0) {
      setMatch(null);
      return;
    }
    const e = email.trim().toLowerCase();
    const p = phone.replace(/\D/g, "").slice(-10);
    const n = fullName.trim().toLowerCase();
    if (!e && !p && !n) {
      setMatch(null);
      return;
    }
    const m = existing.find((c) => {
      if (e && (c.email || "").toLowerCase() === e) return true;
      if (p && (c.phone || "").replace(/\D/g, "").slice(-10) === p && p.length >= 10) return true;
      if (n && (c.full_name || "").toLowerCase() === n) return true;
      return false;
    });
    setMatch(m || null);
  }, [fullName, email, phone, existing]);

  // Household head options exclude the heads-of-other-households' direct members
  // and (trivially) any client that doesn't have their own id
  const headOptions = React.useMemo(() => {
    return (existing || [])
      .filter((c) => !c.household_client_id) // only true heads
      .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));
  }, [existing]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!fullName.trim() || saving) return;
    setSaving(true);
    try {
      const body = {
        full_name: fullName.trim(),
        email: email.trim() || "",
        phone: phone.trim() || undefined,
        household_label: householdLabel.trim() || undefined,
        household_client_id: householdHead || undefined,
      };
      const { data } = await api.post("/travels/clients", body);
      toast.success(`${data.full_name || "Client"} added`);
      onCreated?.(data);
      onClose?.();
      if (navigateToProfile && data?.id) {
        navigate(`/portal/advisor/clients/${data.id}`);
      }
    } catch (err) {
      toast.error(normalizeError(err, "Couldn't create the client."));
    } finally {
      setSaving(false);
    }
  };

  const lockedHeadName = lockHouseholdHead
    ? existing.find((c) => c.id === householdHead)?.full_name
    : null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
          data-testid="new-client-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
            onClick={(ev) => ev.stopPropagation()}
            className="w-full max-w-lg bg-[hsl(var(--background))] rounded-xl shadow-floating"
          >
            <div className="relative px-7 pt-7 pb-3">
              <button
                onClick={onClose}
                className="absolute top-5 right-5 size-8 rounded-full flex items-center justify-center hover:bg-[hsl(var(--background-paper))]"
                data-testid="new-client-close"
                aria-label="Close"
              >
                <X className="size-4" strokeWidth={1.5} />
              </button>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                {lockHouseholdHead ? "Add to household" : "New client"}
              </p>
              <h2 className="mt-2 font-serif text-2xl text-[hsl(var(--foreground))]">
                {lockHouseholdHead && lockedHeadName
                  ? `Add a member to ${lockedHeadName}'s household.`
                  : "Welcome a new household."}
              </h2>
              <p className="mt-2 font-accent italic text-[13.5px] text-[hsl(var(--foreground-secondary))]">
                {lockHouseholdHead
                  ? "Every household member is a first-class client record — same fields, same source of truth."
                  : "A client is the household contact. Add additional members by linking them to a household head."}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="px-7 pb-7 pt-3 space-y-5">
              <div>
                <span className={labelClass}>Full name<span className="text-[hsl(var(--accent-terracotta))] ml-1">*</span></span>
                <input
                  data-testid="new-client-name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="As you'd like to address them"
                  className={inputClass}
                  autoFocus
                  required
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <span className={labelClass}>Email</span>
                  <input
                    data-testid="new-client-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="hello@… (optional)"
                    className={inputClass}
                  />
                </div>
                <div>
                  <span className={labelClass}>Phone</span>
                  <input
                    data-testid="new-client-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 …"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Household head picker — locked inside a Journey, free-choice elsewhere */}
              <div>
                <span className={labelClass}>Household head (optional)</span>
                {lockHouseholdHead ? (
                  <div
                    className="px-3 py-2 rounded-md bg-[hsl(var(--background-paper))]/70 border border-dashed border-[rgba(0,0,0,0.1)] text-[13.5px] font-body"
                    data-testid="new-client-household-head-locked"
                  >
                    {lockedHeadName || "—"}{" "}
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] ml-1">
                      · linked from this journey
                    </span>
                  </div>
                ) : (
                  <select
                    data-testid="new-client-household-head"
                    value={householdHead}
                    onChange={(e) => setHouseholdHead(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">— None · this is a new household</option>
                    {headOptions.map((c, i) => (
                      <option key={`${c.id || "h"}-${i}`} value={c.id}>
                        {c.full_name}
                      </option>
                    ))}
                  </select>
                )}
                <p className="mt-1.5 font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                  Link this client as a member of an existing household, or leave blank to start a new one.
                </p>
              </div>

              {!lockHouseholdHead && !householdHead && (
                <div>
                  <span className={labelClass}>Household label (optional)</span>
                  <input
                    data-testid="new-client-household"
                    type="text"
                    value={householdLabel}
                    onChange={(e) => setHouseholdLabel(e.target.value)}
                    placeholder="The Smith family · Brown-Adler honeymoon · etc."
                    className={inputClass}
                  />
                </div>
              )}

              {match && (
                <div className="px-4 py-3 rounded-md border border-[hsl(var(--accent-gold))]/30 bg-[hsl(var(--accent-gold))]/5 flex gap-3" data-testid="new-client-match-warning">
                  <AlertCircle className="size-4 text-[hsl(var(--accent-gold))] shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">Possible match</p>
                    <p className="font-body text-[13.5px] mt-1">
                      Looks like <span className="font-serif">{match.full_name}</span> may already be on file.
                    </p>
                    <Link
                      to={`/portal/advisor/clients/${match.id}`}
                      onClick={onClose}
                      className="mt-1 inline-flex font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] border-b border-[hsl(var(--accent-gold))]/30 pb-0.5 hover:border-[hsl(var(--accent-gold))]"
                      data-testid="new-client-match-link"
                    >
                      Open their profile →
                    </Link>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-[rgba(0,0,0,0.05)]">
                <PortalButton variant="ghost" size="sm" type="button" onClick={onClose} disabled={saving}>
                  Cancel
                </PortalButton>
                <PortalButton
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={saving || !fullName.trim()}
                  icon={saving ? <Loader2 className="size-3.5 animate-spin" /> : <UserPlus className="size-3.5" strokeWidth={1.5} />}
                  data-testid="new-client-submit"
                >
                  {saving ? "Adding" : "Add client"}
                </PortalButton>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
